// Contact page — the company contact block (placeholders per plan §12.6) plus the
// business-cooperation form (the one interactive feature). Fetches /config.json
// for the Turnstile site key (null => Turnstile disabled, form still works).
//
// Styled with Tailwind utilities + brand tokens to match the rest of the site
// (Services / About / SectionBlock): a soft tint hero band, then a two-column
// layout (company-info column + form) on desktop that stacks on mobile.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import { fetchRuntimeConfig } from "../api/client";
import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "../i18n";
import { fadeUp, stagger, revealViewport } from "../motion/variants";
import SectionEyebrow from "../components/SectionEyebrow";
import ContactForm from "../components/ContactForm";

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

// The company-info <dl> rows, keyed to the existing i18n keys (kept unchanged).
const INFO_ROWS: { labelKey: string; valueKey: string }[] = [
  { labelKey: "info.addressLabel", valueKey: "info.address" },
  { labelKey: "info.phoneLabel", valueKey: "info.phone" },
  { labelKey: "info.emailLabel", valueKey: "info.email" },
  { labelKey: "info.hoursLabel", valueKey: "info.hours" },
];

export default function Contact() {
  const { t } = useTranslation("contact");
  const { lang } = useParams();
  const locale: AppLocale = isLocale(lang) ? lang : DEFAULT_LOCALE;

  // Turnstile site key from runtime config. `undefined` = still loading,
  // `null` = config unavailable or Turnstile disabled (form submits sans token).
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let active = true;
    void fetchRuntimeConfig().then((cfg) => {
      if (active) setTurnstileSiteKey(cfg?.turnstileSiteKey ?? null);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="page page-contact">
      <Helmet>
        <title>{`${t("title")} · ${t("info.companyName")}`}</title>
        <meta name="description" content={t("intro")} />
      </Helmet>

      {/* Hero band — soft tint, bilingual eyebrow + heading (as="h1") + intro. */}
      <section className="relative isolate overflow-hidden bg-[var(--brand-tint-3)]">
        <div className="mx-auto w-full max-w-6xl px-6 py-20">
          <motion.div variants={stagger} initial="hidden" animate="visible" className="max-w-3xl">
            <motion.div variants={fadeUp}>
              <SectionEyebrow eyebrowEn={t("eyebrow")} heading={t("title")} as="h1" />
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mt-5 text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]"
            >
              {t("intro")}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Two-column body: company info (left) + cooperation form (right). */}
      <section className="bg-white py-[var(--space-section)]">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
          {/* Company information column. */}
          <motion.aside
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <motion.h2
              variants={fadeUp}
              className="font-zh text-[var(--text-2xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]"
            >
              {t("info.heading")}
            </motion.h2>

            <motion.dl variants={fadeUp} className="mt-6 space-y-5">
              {/* Only render rows that have a value — fields still 待补充 (phone/email)
                  are left empty in i18n and simply don't show. */}
              {INFO_ROWS.filter((row) => {
                // returnEmptyString:false → empty values echo the key; skip those (待补充).
                const v = t(row.valueKey);
                return v && v !== row.valueKey;
              }).map((row) => (
                <div key={row.labelKey} className="flex flex-col gap-1">
                  <dt className="font-sans text-[var(--text-sm)] font-medium uppercase tracking-[0.12em] text-[var(--brand-azure-text)]">
                    {t(row.labelKey)}
                  </dt>
                  <dd className="text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--ink)]/85">
                    {t(row.valueKey)}
                  </dd>
                </div>
              ))}
            </motion.dl>
          </motion.aside>

          {/* Form column. */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
          >
            {/* Wait for config so we know whether to mount Turnstile. */}
            {turnstileSiteKey !== undefined && (
              <ContactForm locale={locale} turnstileSiteKey={turnstileSiteKey} />
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
